import { env } from "@/configs/env";
import { ApiCode, type GenericApiResponse } from "@/types/api-types";
import ky, { HTTPError } from "ky";
import toast from "react-hot-toast";
import { getSessionToken } from "./auth/utils";
import { logger } from "./logger";

const handleError = async (error: HTTPError) => {
  if (error.response.body) {
    try {
      const body = (await error.response.clone().json()) as GenericApiResponse;
      if (body.msg) {
        error.name = "APIError";
        error.message = body.msg;
      }
    } catch (_e) {}
  }

  logger.error(
    {
      method: error.request.method,
      url: error.request.url,
      status: error.response.status,
      message: error.message,
      response: await error.response.clone().text(),
    },
    "Error occurred during API request",
  );
  if (import.meta.env.SSR === false) {
    toast.error(error.message);
  }
  return error;
};

export const requestor = ky.create({
  hooks: {
    beforeRequest: [
      (request) => {
        logger.trace(
          {
            method: request.method,
            url: request.url,
          },
          "API request",
        );
      },
    ],
    afterResponse: [
      (request, _options, response) => {
        logger.trace(
          {
            method: request.method,
            url: request.url,
            status: response.status,
          },
          "API response",
        );
        return response;
      },
    ],
    beforeError: [handleError],
  },
});

export const api = requestor.extend({
  prefixUrl: env.PUB_API_URL,
  hooks: {
    beforeRequest: [
      async (request) => {
        const sessionToken = await getSessionToken();
        if (sessionToken) {
          request.headers.set("Authorization", `Bearer ${sessionToken}`);
          request.headers.set("Token", sessionToken); // for compatibility
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        const responseJson = (await response
          .clone()
          .json()) as GenericApiResponse;
        if (responseJson.ok && responseJson.code === ApiCode.SUCCESS) {
          return;
        }

        throw await handleError(
          new HTTPError(response.clone(), request, options),
        );
      },
    ],
  },
});
